import React, {useState, useCallback, useRef, useEffect, useMemo} from 'react';
import ReactSwipe from 'react-swipe';
import classNames from 'classnames';
import {useRouteMatch, useHistory} from 'react-router-dom';
import CompanySelector from './components/CompanySelector';
import BarChart from './components/BarChart';
import RadarChart from './components/RadarChart';
import Modal from './components/Modal';
import TextChart from './components/TextChart';
import Awards from './components/Awards';
import NotFound from './components/NotFound';
import Intro from './components/Intro';
import { ReactComponent as IconArrow } from './assets/images/icon_arrow.svg';

import companies, {getCompanyById} from './assets/companies.data';
import years, {getIndexByYear, getScriptsByYears, getYearsByYear} from './assets/years.data';

import cx from './App.module.scss';
import GNB from './components/GNB';
import SearchButton from './components/SearchButton';

let timer;

const agent = navigator.userAgent.toLowerCase();
const isIE = (navigator.appName === 'Netscape' && agent.indexOf('trident') !== -1) || (agent.indexOf("msie") !== -1);

const getRandomNumber = (length) => {
  return Math.floor(Math.random() * length);
};

const handleSlideStart = (index, elem) => {
  if (isIE) {
    const script = elem.querySelector(`.${cx.scripts}.${cx.isActive}`);
    if (script) script.className = script.className.replace(cx.isActive, '');
  } else {
    const scripts = elem.getElementsByClassName(cx.scripts);
    if (scripts && scripts.length) {
      for (const script of Array.from(scripts)) {
        script.className = script.className.replace(cx.isActive, '');
        const images = script.getElementsByClassName(cx.script);
        for (const image of images) {
          image.className = image.className.replace(cx.isActive, '');
        }
      }
    }
  }
};

const handleSlideEnd = (index, elem) => {
  const scripts = elem.getElementsByClassName(cx.scripts);
  const randomNumber = getRandomNumber(scripts.length);
  if (scripts && scripts.length) {
    const script = Array.from(scripts)[randomNumber];
    script.className = `${script.className} ${cx.isActive}`;
    if (isIE) return;
    const images = script.getElementsByClassName(cx.script);
    if (images && images.length) {
      for (const [index, image] of [...images].entries()) {
        setTimeout(() => {
          image.className = `${image.className} ${cx.isActive}`;
        }, index * 200);
      }
    }
  }
};

const swipeOptions = {
  continuous: false,
  draggable: false,
  disableScroll: true,
  callback: handleSlideStart,
  transitionEnd: handleSlideEnd,
};

const renderScripts = (years) => {
  const scripts = getScriptsByYears(years);
  return scripts.map((script, index) => (
    <div className={classNames(cx.scripts)} key={`script-${index}`}>
      {script.map((image) => {
        const [fileName, x, y, width] = image.split(',');
        return (
          <img
            className={classNames(cx.script, {[cx.isActive]: isIE})}
            style={{
              width: `${Math.floor((width / 375) * 100)}%`,
              top: `${Math.floor((y / 300) * 100)}%`,
              left: `${Math.floor((x / 375) * 100)}%`,
            }}
            key={`script-image-${fileName}`}
            src={`${process.env.PUBLIC_URL}/slides/${fileName}.svg`}
          />
        )
      })}
    </div>
  ))
};

const slides = years.map(item => (
  <div className={cx.carouselItem} key={`carousel-item-${item}`}>
    <div className={cx.inner} />
    <div className={cx.contents} style={{backgroundImage: `url(${process.env.PUBLIC_URL}/slides/ill_${item}.png)`}} />
    {renderScripts(item)}
  </div>
));

const App = () => {
  const isYears = useRouteMatch({
    path: '/years/:years',
    strict: true,
  });
  const {params} = useRouteMatch();
  const {push} = useHistory();
  const carouselRef = useRef(null);
  const [isFontLoaded, setIsFontLoaded] = useState(false);
  const [modal, setModal] = useState(null);

  const company = useMemo(() => {
    if (params && params.companyId) return getCompanyById(params.companyId);
  }, [params]);

  const clickedYears = useMemo(() => {
    if (isYears && isYears.isExact) return isYears.params.years;
  }, [isYears]);

  const slideIndex = useMemo(() => {
    if (company) return getIndexByYear(company.year);
    if (clickedYears) return getIndexByYear(clickedYears);
    return null
  }, [company, clickedYears]);

  useEffect(() => {
    const handleOnLoadingDone = () => {
      setTimeout(() => setIsFontLoaded(true), 250);
    };
    if (document.fonts) document.fonts.onloadingdone = handleOnLoadingDone;
    timer = setTimeout(handleOnLoadingDone, 500);
    return () => timer && clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (params && params.companyId) {
      const company = getCompanyById(params.companyId);
      (company && carouselRef.current) && carouselRef.current.slide(getIndexByYear(company.year));
    }
  }, [params]);

  useEffect(() => {
    if (isYears && isYears.params && isYears.params.years) {
      carouselRef.current && carouselRef.current.slide(getIndexByYear(isYears.params.years));
    }
  }, [isYears]);

  const handleCloseModal = useCallback(event => {
    setModal(null);
  }, [setModal]);

  const handleOnSelectCompany = useCallback((company) => {
    if (company) {
      push(`/${company.id}`);
      handleCloseModal();
      const newIndex = getIndexByYear(company.year);
      carouselRef.current.slide(newIndex);
    } else {
      setModal(<NotFound />);
    }
  }, [handleCloseModal, setModal, push, company]);

  const handleClickSearch = useCallback(() => {
    setModal(<CompanySelector onSubmit={handleOnSelectCompany} data={companies} />)
  }, [setModal, handleOnSelectCompany]);

  const handleClickBar = useCallback(years => {
    push(`/years/${years}`);
    const newIndex = getIndexByYear(years);
    carouselRef.current.slide(newIndex);
  }, [push]);

  const handleGoToHome = useCallback(() => {
    push('/');
  }, [push]);

  const handleClickPrev = useCallback(() => {
    handleClickBar(years[slideIndex - 1]);
  }, [handleClickBar, slideIndex]);

  const handleClickNext = useCallback(() => {
    handleClickBar(years[slideIndex + 1]);
  }, [handleClickBar, slideIndex]);

  const appStyle = useMemo(() => ({
    opacity: isFontLoaded ? 1 : 0,
    transform: `translateY(${isFontLoaded ? 0 : '20px'})`,
  }), [isFontLoaded]);

  return (
    <div className={cx.app} style={appStyle}>
      {(company || clickedYears) ? (
        <header className={cx.appHeader}>
          <GNB onClick={handleGoToHome} />
          <div className={cx.appHeaderInner}>
            <SearchButton onSearch={handleClickSearch} isSmall={true} />
            <div className={cx.title}>
              {company && (<div className={cx.companyName}>{company.name}</div>)}
              {company ? (
                <>의 문화는 <strong className={cx.strong}>{getYearsByYear(company.year)}년대</strong></>
              ) : (
                <><strong className={cx.brand}>{clickedYears}</strong><strong className={cx.strong}>년대의<br/>회사</strong></>
              )}
            </div>
          </div>
        </header>
      ) : (
        <header className={classNames(cx.appHeader, cx.isHome)}>
          <GNB />
          <Intro onSearch={handleClickSearch} company={company} setModal={setModal} />
        </header>
      )}
      <div className={cx.appBody}>
        <div className={cx.carouselWrap}>
          <ReactSwipe
            className={classNames(cx.carousel, {
              [cx.isVisible]: (company || clickedYears),
            })}
            swipeOptions={swipeOptions}
            childCounts={years.length}
            ref={carouselRef}
          >
            {slides}
          </ReactSwipe>
          {(company || clickedYears) && (
            <>
              {slideIndex === 0 || (
                <button className={cx.buttonPrev} onClick={handleClickPrev}>
                  <IconArrow className={cx.iconArrow} alt="이전으로" />
                </button>
              )}
              {slideIndex === years.length - 1 || (
                <button className={cx.buttonNext} onClick={handleClickNext}>
                  <IconArrow className={cx.iconArrow} alt="다음으로" />
                </button>
              )}
            </>
          )}
        </div>
        {(company || clickedYears) && <TextChart company={company} clickedYears={clickedYears} setModal={setModal} />}
        <BarChart slideIndex={slideIndex} clickedYears={clickedYears} company={company} onClick={handleClickBar} />
        {!((company || clickedYears)) && <Awards onClick={handleOnSelectCompany} />}
        {company && <RadarChart company={company} />}
      </div>
      {modal && (
        <Modal onClose={handleCloseModal}>
          {modal}
        </Modal>
      )}
    </div>
  );
};

export default App;
